/**
 * A edição do cadastro e do contato financeiro no detalhe (#769), sem tela.
 *
 * A conferência daqui é de formato, para o aviso aparecer na hora. Se o CNPJ
 * já é de outra organização, e se o telefone existe, quem diz é o servidor.
 *
 * Os dois pedidos levam só o que mudou desde que a edição abriu. O contato vai
 * inteiro quando qualquer parte dele muda: nome, e-mail e telefone são de uma
 * pessoa só.
 */

import type { AlterarCadastroDaOrganizacao } from '@cliqcrm/contracts';
import { cnpjSemMascara, cnpjValido, mascararCnpj } from '@/shared/lib/cnpj';
import { telefoneParaLer } from '@/shared/lib/formato';
import type {
  ContatoFinanceiro,
  MudancaDoCadastro,
  OrganizacaoDetalhada,
} from '../model/organizacao-detalhada';
import { SEGMENTOS } from '../model/organizacao-detalhada';

export type CamposDoCadastro = {
  readonly razaoSocial: string;
  readonly cnpj: string;
  readonly segmento: string;
};

export type CamposDoContato = {
  readonly nome: string;
  readonly email: string;
  readonly telefone: string;
};

export type Erros<C> = Partial<Record<keyof C, string>>;

type Leitura<C> =
  | { readonly ok: true; readonly mudanca: MudancaDoCadastro }
  | { readonly ok: false; readonly erros: Erros<C> };

const EMAIL = /^\S+@\S+\.\S+$/;
const OBRIGATORIO = 'Preencha este campo.';

/** Os campos do cadastro como a edição abre: o que está gravado, com a máscara. */
export function camposDoCadastro(cadastro: OrganizacaoDetalhada['cadastro']): CamposDoCadastro {
  return {
    razaoSocial: cadastro.razaoSocial ?? '',
    cnpj: cadastro.cnpj ? mascararCnpj(cadastro.cnpj) : '',
    segmento: cadastro.segmento?.valor ?? '',
  };
}

/** A edição do cadastro vira o pedido, só com o que mudou, ou o motivo de cada campo. */
export function lerCadastro(
  campos: CamposDoCadastro,
  abertos: CamposDoCadastro,
): Leitura<CamposDoCadastro> {
  const erros: Erros<CamposDoCadastro> = {};
  const razaoSocial = campos.razaoSocial.trim();
  if (razaoSocial !== abertos.razaoSocial.trim() && razaoSocial.length < 2) {
    erros.razaoSocial = razaoSocial === '' ? OBRIGATORIO : 'Escreva a razão social inteira.';
  }
  const cnpjMudou = cnpjSemMascara(campos.cnpj) !== cnpjSemMascara(abertos.cnpj);
  if (cnpjMudou && !cnpjValido(campos.cnpj)) {
    erros.cnpj = campos.cnpj.trim() === '' ? OBRIGATORIO : 'Confira o CNPJ: os dígitos não batem.';
  }
  if (Object.keys(erros).length > 0) return { ok: false, erros };

  const segmento = SEGMENTOS.find((opcao) => opcao.valor === campos.segmento)?.valor;
  return {
    ok: true,
    mudanca: {
      ...(razaoSocial !== abertos.razaoSocial.trim() ? { razaoSocial } : {}),
      ...(cnpjMudou ? { cnpj: cnpjSemMascara(campos.cnpj) } : {}),
      ...(segmento && campos.segmento !== abertos.segmento ? { segmento } : {}),
    },
  };
}

/** Os campos do contato como a edição abre: o telefone como gente lê. */
export function camposDoContato(contato: ContatoFinanceiro | null): CamposDoContato {
  return {
    nome: contato?.nome ?? '',
    email: contato?.email ?? '',
    telefone: contato ? telefoneParaLer(contato.telefone) : '',
  };
}

/** A edição do contato vira o pedido, com o contato inteiro, ou o motivo de cada campo. */
export function lerContato(
  campos: CamposDoContato,
  abertos: CamposDoContato,
): Leitura<CamposDoContato> {
  const contato = {
    nome: campos.nome.trim(),
    email: campos.email.trim(),
    telefone: campos.telefone.trim(),
  };
  const mudou =
    contato.nome !== abertos.nome.trim() ||
    contato.email !== abertos.email.trim() ||
    contato.telefone !== abertos.telefone.trim();
  if (!mudou) return { ok: true, mudanca: {} };

  const erros: Erros<CamposDoContato> = {};
  if (contato.nome.length < 2) erros.nome = contato.nome === '' ? OBRIGATORIO : 'Escreva o nome.';
  if (!EMAIL.test(contato.email)) {
    erros.email = 'Escreva um e-mail completo, como nome@empresa.com.br.';
  }
  if (contato.telefone.replace(/\D/g, '').length < 10) {
    erros.telefone = 'Escreva o telefone com o DDD, como (11) 98765-4321.';
  }
  if (Object.keys(erros).length > 0) return { ok: false, erros };
  return { ok: true, mudanca: { contatoFinanceiro: contato } };
}

/** O corpo de `PATCH /v1/operacao/organizacoes/{id}/cadastro`. */
export function corpoDoCadastro(mudanca: MudancaDoCadastro): AlterarCadastroDaOrganizacao {
  return {
    ...(mudanca.razaoSocial === undefined ? {} : { razao_social: mudanca.razaoSocial }),
    ...(mudanca.cnpj === undefined ? {} : { cnpj: mudanca.cnpj }),
    ...(mudanca.segmento === undefined ? {} : { segmento: mudanca.segmento }),
    ...(mudanca.contatoFinanceiro === undefined
      ? {}
      : { contato_financeiro: mudanca.contatoFinanceiro }),
  };
}
